import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Search, Rocket, CreditCard, Wand2, BarChart3, Layers, Shield,
  HelpCircle, MessageSquare, ArrowRight,
} from "lucide-react";
import MarketingShell from "../components/marketing/MarketingShell";

const CATEGORIES = [
  { id: "getting-started", icon: Rocket,    title: "Getting started",   desc: "Onboarding, first listing, account setup." },
  { id: "billing",          icon: CreditCard, title: "Billing & plans",   desc: "Plans, upgrades, invoices." },
  { id: "listings",         icon: Wand2,      title: "AI Listings",       desc: "Auto-fill, categories, HSN, validation." },
  { id: "analytics",        icon: BarChart3,  title: "Analytics",         desc: "Reports, dashboards, exports." },
  { id: "bulk",             icon: Layers,     title: "Bulk & API",        desc: "Folder upload, queues, API tokens." },
  { id: "marketplaces",     icon: HelpCircle, title: "Marketplaces",      desc: "Per-platform setup & exports." },
  { id: "security",         icon: Shield,     title: "Security & data",   desc: "Data isolation, export, account deletion." },
];

const ARTICLES = [
  { cat: "getting-started", q: "How do I create my account?",       a: "Click 'Get Started' on any page → enter email + password → contact our sales team for plan activation. You'll be onboarded to the seller dashboard once your plan is set up." },
  { cat: "getting-started", q: "What's included in the Starter plan?", a: "50 AI listings/month and the full Smart Analytics dashboard." },
  { cat: "getting-started", q: "How do I list my first SKU?",       a: "Open Auto Listing → drop a product photo → click AI Auto-Fill → confirm sizes → Generate Excel. The download is marketplace-ready." },
  { cat: "billing",         q: "Is there a trial?",                  a: "Yes. Every plan starts with a guided onboarding walkthrough. Talk to sales to scope the right tier for your SKU volume and marketplaces." },
  { cat: "billing",         q: "Do you support GST invoicing?",     a: "No. All plans (Starter ₹2,499 / Pro ₹4,999 / Business ₹8,999) are billed WITHOUT GST. We don't add GST on top, and the invoice issued to you will not include any GST component. If you need a GST-input-credit invoice, please reach out to business@brandborn.in BEFORE paying so we can discuss." },
  { cat: "billing",         q: "How do I upgrade?",                  a: "Profile → Plans → Upgrade. You'll be redirected to a secure Stripe checkout. Prorated billing applies." },
  { cat: "listings",        q: "Why was my Myntra listing rejected?", a: "The most common reasons are HSN mismatch, wrong image size or missing size chart. EcomCatalog AI pre-validates all 12 common rejection causes before export — see our 'Top 12 rejection reasons' blog." },
  { cat: "listings",        q: "Which categories are supported?",   a: "All 225+ Myntra categories (Apparel, Footwear, Beauty, Home & Kids, Accessories). Nykaa, Ajio, Amazon and Flipkart use AI-mapped equivalents." },
  { cat: "listings",        q: "Can I edit AI auto-filled fields?",  a: "Yes. Every field is editable before Generate. The AI suggestions are pre-populated — you stay in control." },
  { cat: "analytics",       q: "What goes in the weekly report?",    a: "Six sheets: Top SKUs, Bottom SKUs, Ads Plan (SCALE/TEST/AVOID), Quick Commerce, Cancellation Reasons, and your raw cleaned data." },
  { cat: "analytics",       q: "Can I email the report to a client?", a: "Yes. Reports → Send → enter the recipient email. Or schedule weekly auto-delivery in Profile → Notifications." },
  { cat: "bulk",            q: "How does folder upload work?",        a: "Drop a parent folder containing sub-folders (one sub-folder = one SKU, 1-7 images each). AI processes all SKUs in parallel and exports one Excel." },
  { cat: "bulk",            q: "Do you have an API?",                 a: "API access is included in the Business plan. We expose listing creation and analytics endpoints. Contact sales for keys." },
  { cat: "marketplaces",    q: "Which marketplaces are supported?",   a: "Myntra, Nykaa, Ajio, Amazon India, Flipkart, Swiggy Instamart and Blinkit." },
  { cat: "marketplaces",    q: "Can you push directly to Seller Central?", a: "Direct write integrations (Amazon SP-API, Flipkart Listings API) are scheduled for Q3 2026. Today we generate flat-files for upload." },
  { cat: "security",        q: "Is my catalog data isolated?",        a: "Yes. Single-tenant data isolation per seller. No cross-brand training. You can export everything as JSON/CSV any time." },
  { cat: "security",        q: "How do I delete my account?",         a: "Profile → Account → Delete account. All data is purged within 7 days. Email us if you need an immediate hard delete." },
];

export default function HelpCenter() {
  const [q, setQ] = useState("");
  const [activeCat, setActiveCat] = useState(null);

  useEffect(() => {
    document.title = "Help Center — EcomCatalog AI · Guides, FAQs & Support";
  }, []);

  const results = useMemo(() => {
    const Q = q.trim().toLowerCase();
    return ARTICLES.filter((a) => {
      if (activeCat && a.cat !== activeCat) return false;
      if (!Q) return true;
      return a.q.toLowerCase().includes(Q) || a.a.toLowerCase().includes(Q);
    });
  }, [q, activeCat]);

  return (
    <MarketingShell>
      {/* Hero search */}
      <section className="pt-16 sm:pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center">
          <div className="text-[11px] uppercase tracking-[0.3em] text-indigo-400 font-bold">
            Help Center
          </div>
          <h1 className="font-mkt-display text-5xl sm:text-6xl md:text-7xl font-semibold tracking-tighter mt-4 leading-[1.02]">
            How can we <span className="mkt-gradient-text-vivid">help?</span>
          </h1>
          <div className="mt-10 relative">
            <Search size={18} className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search articles, guides, FAQs..."
              className="w-full pl-14 pr-5 py-5 bg-white/[0.03] border border-white/[0.12] rounded-2xl text-white text-base focus:outline-none focus:border-indigo-400/60 transition placeholder:text-slate-500"
              data-testid="help-search"
            />
          </div>
          <div className="mt-3 text-xs text-slate-500">
            {q || activeCat ? `${results.length} article(s) match` : `${ARTICLES.length} articles in ${CATEGORIES.length} categories`}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="pb-16">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {CATEGORIES.map((c) => {
              const active = activeCat === c.id;
              return (
                <button
                  key={c.id}
                  onClick={() => setActiveCat(active ? null : c.id)}
                  className={`mkt-glass mkt-hover-lift p-5 text-left transition-all ${
                    active ? "!border-indigo-400/50 !bg-indigo-500/[0.08]" : ""
                  }`}
                  data-testid={`help-cat-${c.id}`}
                >
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500/20 to-purple-500/20 border border-indigo-400/30 grid place-items-center mb-3">
                    <c.icon size={16} className="text-indigo-300" />
                  </div>
                  <div className="font-mkt-display text-base font-semibold tracking-tight">{c.title}</div>
                  <div className="text-xs text-slate-400 mt-1.5 leading-relaxed">{c.desc}</div>
                </button>
              );
            })}
          </div>
          {activeCat && (
            <div className="mt-4 text-xs text-slate-400">
              Showing: <span className="text-indigo-300 font-semibold">{CATEGORIES.find((c) => c.id === activeCat)?.title}</span>
              {" · "}
              <button onClick={() => setActiveCat(null)} className="underline hover:text-white">
                clear filter
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Articles list */}
      <section className="pb-20">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <div className="mkt-glass">
            {results.length === 0 ? (
              <div className="p-10 text-center text-slate-400">
                No matches for "<span className="text-white">{q}</span>".
                <div className="mt-2 text-sm">Try a broader keyword or{" "}
                  <Link to="/contact" className="text-indigo-300 hover:underline">contact support</Link>.
                </div>
              </div>
            ) : (
              results.map((a, i) => (
                <details
                  key={i}
                  className="border-b border-white/[0.06] last:border-b-0 group"
                  data-testid={`help-article-${i}`}
                >
                  <summary className="px-5 sm:px-7 py-5 cursor-pointer list-none flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <span className="text-[10px] uppercase tracking-[0.2em] text-indigo-300 font-bold w-32 shrink-0">
                        {CATEGORIES.find((c) => c.id === a.cat)?.title}
                      </span>
                      <span className="text-sm font-medium text-white">{a.q}</span>
                    </div>
                    <ArrowRight
                      size={16}
                      className="text-slate-400 group-open:rotate-90 transition-transform shrink-0"
                    />
                  </summary>
                  <div className="px-5 sm:px-7 pb-5 -mt-1 text-sm text-slate-400 leading-relaxed max-w-3xl">
                    {a.a}
                  </div>
                </details>
              ))
            )}
          </div>
        </div>
      </section>

      {/* Still need help */}
      <section className="pb-12">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <div className="mkt-glass-strong p-10 sm:p-12 flex flex-col sm:flex-row items-center justify-between gap-6 text-center sm:text-left">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-indigo-300 font-bold">Still stuck?</div>
              <h3 className="font-mkt-display text-3xl font-semibold tracking-tight mt-2">
                Talk to a human.
              </h3>
              <p className="text-slate-400 mt-2 text-sm">
                Our team replies within 24 hours on every email.
              </p>
            </div>
            <Link to="/contact" className="mkt-btn-gradient" data-testid="help-contact-cta">
              Contact Support <MessageSquare size={14} />
            </Link>
          </div>
        </div>
      </section>
    </MarketingShell>
  );
}
